<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  text: { type: String, default: '' },
  label: { type: String, default: '' },
  copyingLabel: { type: String, default: '' },
  copiedLabel: { type: String, default: '' },
  errorLabel: { type: String, default: '' },
  unavailableLabel: { type: String, default: '' },
  emptyLabel: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  iconOnly: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['copied', 'error']);

const { t } = useI18n();
const copied = ref(false);
const failed = ref(false);
const isCopying = ref(false);
let resetTimer;

const label = computed(() => props.label || t('LAIS_COPY_BUTTON.LABEL'));
const copyingLabel = computed(
  () => props.copyingLabel || t('LAIS_COPY_BUTTON.COPYING')
);
const copiedLabel = computed(
  () => props.copiedLabel || t('LAIS_COPY_BUTTON.COPIED')
);
const errorLabel = computed(
  () => props.errorLabel || t('LAIS_COPY_BUTTON.ERROR')
);
const unavailableLabel = computed(
  () => props.unavailableLabel || t('LAIS_COPY_BUTTON.UNAVAILABLE')
);
const emptyLabel = computed(
  () => props.emptyLabel || t('LAIS_COPY_BUTTON.EMPTY')
);
const tooltipLabel = computed(() => t('LAIS_COPY_BUTTON.TOOLTIP'));

const hasCopyTarget = computed(() => Boolean(props.text));
const hasClipboard = () =>
  Boolean(navigator.clipboard?.writeText || document.execCommand);
const isDisabled = computed(
  () =>
    props.disabled || isCopying.value || !hasCopyTarget.value || !hasClipboard()
);
const disabledReason = computed(() => {
  if (!hasCopyTarget.value) return emptyLabel.value;
  if (!hasClipboard()) return unavailableLabel.value;
  return '';
});
const displayLabel = computed(() => {
  if (isCopying.value) return copyingLabel.value;
  if (copied.value) return copiedLabel.value;
  if (failed.value) return errorLabel.value;
  return label.value;
});
const accessibleLabel = computed(
  () =>
    props.ariaLabel ||
    disabledReason.value ||
    (props.iconOnly ? tooltipLabel.value : displayLabel.value)
);
const statusMessage = computed(() => {
  if (copied.value) return copiedLabel.value;
  if (failed.value) return errorLabel.value;
  return '';
});
const iconClass = computed(() => {
  if (isCopying.value) return 'i-lucide-loader-circle animate-spin';
  if (copied.value) return 'i-lucide-check text-lais-primary';
  if (failed.value) return 'i-lucide-circle-alert text-lais-danger';
  return 'i-lucide-copy';
});

const resetStatus = () => {
  copied.value = false;
  failed.value = false;
};

const scheduleReset = () => {
  clearTimeout(resetTimer);
  resetTimer = setTimeout(resetStatus, 2000);
};

const writeClipboard = async value => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', 'true');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();

  let copiedWithFallback = false;
  try {
    copiedWithFallback = document.execCommand('copy');
  } finally {
    document.body.removeChild(input);
  }

  if (!copiedWithFallback) {
    throw new Error('clipboard_copy_failed');
  }
};

const copy = async () => {
  if (isDisabled.value) return;

  isCopying.value = true;
  resetStatus();

  try {
    await writeClipboard(props.text);
    copied.value = true;
    useAlert(copiedLabel.value, null, { variant: 'success' });
    emit('copied', props.text);
  } catch (error) {
    failed.value = true;
    useAlert(errorLabel.value, null, { variant: 'error' });
    emit('error', error);
  } finally {
    isCopying.value = false;
    scheduleReset();
  }
};

onBeforeUnmount(() => {
  clearTimeout(resetTimer);
});
</script>

<template>
  <button
    type="button"
    :class="[
      iconOnly
        ? 'size-4 p-0 text-lais-primary transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:shadow-lais-focus'
        : 'min-h-8 gap-1.5 rounded-full border border-lais-border bg-white px-3 py-1 text-[12px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:bg-lais-primary-50 focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-lais-border disabled:hover:bg-white',
      {
        'border-lais-primary bg-lais-primary-50 text-lais-primary':
          copied && !iconOnly,
        'text-lais-primary': copied && iconOnly,
        'border-lais-danger text-lais-danger': failed && !iconOnly,
        'text-lais-danger': failed && iconOnly,
      },
    ]"
    :title="accessibleLabel"
    :aria-label="accessibleLabel"
    :aria-busy="isCopying ? 'true' : undefined"
    :disabled="isDisabled"
    @click="copy"
  >
    <span
      :class="[iconClass, iconOnly ? 'size-4' : 'size-3.5']"
      aria-hidden="true"
    />
    <span v-if="!iconOnly">{{ displayLabel }}</span>
    <span class="sr-only" aria-live="polite" aria-atomic="true">
      {{ statusMessage }}
    </span>
  </button>
</template>
